import { useOrders } from '@api/useOrders'
import { carrierDispatchPageConfig } from '@components/OrderMovements/OrderMovements/MovementDetail/BOL/Detail/constants'
import { RequestedChangesDrawer } from '@components/_SiteWide/AuditHistory/RequestedChangesDrawer'
import { GraviButton, GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { Tooltip, notification } from 'antd'
import { addDays, subDays } from 'date-fns'
import moment from 'moment'
import queryString from 'query-string'
import { type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { getColumnDefs } from '../components/Grid/Columns/CarrierDispatchColDefs'
import { orderViewColumns } from '../components/Grid/Columns/OrderColumnCellrenderers'
import { CarrierDispatchActionBtns } from '../components/Grid/HeaderControls/CarrierDispatchActionBtns'
import { useOrderManagementContext } from '../context'
import { getOrderProductRowData, getRowId, getStorageKey, sendToCarriers } from '../util'
import { isRowSelectable } from './util'
import { gridViewManager } from '@/utils/grid'
import { DetailRenderer } from '../components/Grid/Columns/DetailRenderer'
import { detailsColumnDefs } from '../components/Grid/Columns/DefaultColumnDefs'

import type { OrderManagementGridView } from '../All/types'
import type { ICellRendererParams } from 'ag-grid-community'

const stickyStatus = localStorage.getItem('order_management_od_status') || 'all_od'
export function CarrierDispatch() {
  const [isPopoverVisible, setIsPopoverVisible] = useState('')
  const { updateSetting } = useUserSettings()

  const { value: orderManagementDayRange, setValue: setOMDayRange } = useLocalStorage('order-management-od-range', {
    back: -3,
    ahead: 1,
  })
  const setOrderManagementDayRange = async (value: SetStateAction<{ back: number; ahead: number }>) => {
    setOMDayRange(value)
    await updateSetting('order-management-od-range', value)
  }
  const [filters, setFilters] = useState({
    ...carrierDispatchPageConfig?.[stickyStatus]?.filters,
    From: moment(
      subDays(new Date(), orderManagementDayRange.back ? Math.abs(orderManagementDayRange.back) : 3)
    ).startOf('day'),
    To: moment(addDays(new Date(), orderManagementDayRange.ahead ? orderManagementDayRange.ahead : 1)).endOf('day'),
  })
  const [orderStatus, setOrderStatus] = useState(stickyStatus)
  const [selectedRows, setSelectedRows] = useState([])
  const [isSending, setIsSending] = useState(false)
  const [changeId, setChangeId] = useState()
  const [hasStagedOrders, setHasStagedOrders] = useState(0)
  const { sendCarrierOrderMutation, stageCarrierOrderMutation } = useOrders()
  const { mutateAsync } = sendCarrierOrderMutation

  const { scopes } = useSecurityContext()
  const {
    gridAPIRef,
    showModal,
    selectedOrderId,
    setOrderHistoryStoreId,
    setAuditOrderId,
    triggerTPDModal,
    handleTPDDismissModal,
    handleOrderChange,
  } = useOrderManagementContext()

  const setupQueryParams = (status: string) => {
    const defaultFilter: Record<string, any> = {}
    const orderNumber = queryString.parse(window.location.search).order_number

    if (orderNumber) defaultFilter.order_number = orderNumber

    setFilters({
      ...carrierDispatchPageConfig?.[status]?.filters,
      Order: orderNumber,
      From: moment(
        addDays(new Date(), orderManagementDayRange.back ? parseInt(orderManagementDayRange.back) : -3)
      ).startOf('day'),
      To: moment(
        addDays(new Date(), orderManagementDayRange.ahead ? parseInt(orderManagementDayRange.ahead) : 1)
      ).endOf('day'),
    })
  }

  useEffect(() => {
    const status = localStorage.getItem('order-management-od-status')
    if (status) {
      setOrderStatus(status)
      setupQueryParams(status)
    }
  }, [])

  const onSelectionChanged = useCallback((event: { api: { getSelectedNodes: () => any } }) => {
    const nodes = event.api.getSelectedNodes()
    const rowIds = nodes.map((node: { id: any }) => node.id)
    setSelectedRows(rowIds)
    setHasStagedOrders(
      nodes.filter((node: { data: { carrier_notify_state: string } }) => node.data?.carrier_notify_state === 'staged')
        .length
    )
  }, [])

  const handleSendToCarriers = async (instant = false) => {
    setIsSending(true)
    await sendToCarriers(selectedRows, gridAPIRef, mutateAsync, instant)
    gridAPIRef.current?.deselectAll()
    setIsSending(false)
  }

  const { useOrdersQuery } = useOrders()
  const { data: ordersData, isPending: isLoadingOrders } = useOrdersQuery(filters)
  const hiddenFilterKeys = ['assignment', 'state', 'from', 'to', 'notification_state']
  const manualPO = useMemo(() => ordersData?.manual_po, [ordersData])

  const { value: selectedView, setValue: setSelectedView } = useLocalStorage(
    'orderManagement::selectedView',
    'order' as OrderManagementGridView
  )
  const isByOrderView = selectedView === 'order'
  const columnDefs = useMemo(
    () =>
      isByOrderView
        ? getColumnDefs(
            setChangeId,
            showModal,
            selectedOrderId,
            scopes,
            setOrderHistoryStoreId,
            setAuditOrderId,
            triggerTPDModal,
            handleTPDDismissModal,
            orderStatus,
            gridAPIRef,
            setIsPopoverVisible,
            isPopoverVisible,
            manualPO
          )
        : detailsColumnDefs(showModal),
    [ordersData, isPopoverVisible, orderStatus, manualPO, isByOrderView]
  )

  const agPropsOverrides = useMemo(
    () => ({
      getRowId: (params: ICellRendererParams) => getRowId(params.data, isByOrderView),
      suppressCellSelection: true,
      frameworkComponents: orderViewColumns,
      rowGroupPanelShow: 'always',
      rowSelection: 'multiple',
      isRowSelectable,
      groupSelectsChildren: true,
      suppressRowDeselection: true,
      suppressRowClickSelection: true,
      detailRowAutoHeight: true,
    }),
    [isRowSelectable, isByOrderView]
  )
  const handleStageOrders = async () => {
    if (!selectedRows?.length) return
    setIsSending(true)
    try {
      await stageCarrierOrderMutation.mutateAsync(selectedRows)
      const row = gridAPIRef?.current.getRowNode(selectedRows[0])
      notification.success({
        message: 'Carrier Orders Staged',
        description:
          selectedRows?.length > 1
            ? `${selectedRows?.length} orders have been staged.`
            : `Order ${row?.data?.number} has been staged.`,
        duration: 6,
        placement: 'topRight',
        key: 'carrier-stage-notification',
      })
      gridAPIRef.current?.refreshCells()
    } catch (error) {
      notification.error({
        message: 'Carrier Order Update Failed',
        description: 'Please try again or contact your system administrator if this continues.',
        duration: 6,
        placement: 'topRight',
        key: 'carrier-error-notification',
      })
      console.error(error)
    } finally {
      gridAPIRef.current?.deselectAll()
      setIsSending(false)
    }
  }

  const rowData = useMemo(() => getOrderProductRowData(ordersData, isByOrderView), [ordersData, isByOrderView])
  const storageKey = getStorageKey('carrier-order-management', isByOrderView)

  return (
    <>
      <RequestedChangesDrawer
        changeId={changeId}
        setChangeId={setChangeId}
        onClose={() => setChangeId(undefined)}
        onOrderChange={handleOrderChange}
      />
      <GraviGrid
        key={storageKey}
        onSelectionChanged={onSelectionChanged}
        columnDefs={columnDefs}
        controlBarProps={{
          title: 'Carrier Dispatch Orders',
          serverParams: ordersData?.params,
          filters,
          setFilters,
          actionButtons: (
            <>
              <CarrierDispatchActionBtns
                gridAPIRef={gridAPIRef}
                setOrderStatus={setOrderStatus}
                orderStatus={orderStatus}
                filters={filters}
                setFilters={setFilters}
                getDatesFromStorage
                orderManagementDayRange={orderManagementDayRange}
                setOrderManagementDayRange={setOrderManagementDayRange}
                updateSetting={updateSetting}
                selectedView={selectedView}
                setSelectedView={setSelectedView}
              />
              {selectedRows?.length > 0 && (
                <Horizontal>
                  <GraviButton
                    theme2
                    loading={isSending}
                    buttonText={`Send ${selectedRows?.length} Order${selectedRows?.length > 1 ? 's' : ''} Now`}
                    className='ml-3'
                    onClick={() => handleSendToCarriers(true)}
                  />
                  <Tooltip
                    placement='topLeft'
                    overlayStyle={hasStagedOrders ? { minWidth: '290px' } : {}}
                    title={
                      hasStagedOrders > 0 ? `Cannot stage already staged orders. (${hasStagedOrders} Selected)` : ''
                    }
                  >
                    <GraviButton
                      loading={isSending}
                      buttonText={`Stage ${selectedRows?.length} Order${selectedRows?.length > 1 ? 's' : ''}`}
                      className='ml-3'
                      onClick={() => hasStagedOrders < 1 && handleStageOrders()}
                      style={
                        hasStagedOrders > 0
                          ? {
                              cursor: 'not-allowed',
                              backgroundColor: '#f0f0f0',
                              color: '#ccc',
                            }
                          : {}
                      }
                    />
                  </Tooltip>
                </Horizontal>
              )}
            </>
          ),
          defaultFilter: { From: subDays(new Date(), 2), To: new Date() },
          hiddenFilterKeys,
        }}
        agPropOverrides={agPropsOverrides}
        externalRef={gridAPIRef}
        storageKey={storageKey}
        loading={isLoadingOrders}
        rowData={rowData}
        enableRangeSelection={false}
        onGridConfigChanged={(config: any) => updateSetting(`gridConfig::${storageKey}`, config)}
        masterDetail={isByOrderView}
        detailCellRenderer={DetailRenderer}
        {...gridViewManager(storageKey, updateSetting)}
      />
    </>
  )
}
